<template>
  <UIPage
    class="WAM-page"
    scrollable
  >
    <!-- check permissions for the "Create" button -->
    <SubHeader
      back-button
      :back-redirection-route="{ name: 'Work Allocation Matrix List' }"
    />
    <!-- TODO -->
    <NeoLayout
      v-if="currentWAM"
      class="WAM-page__content"
      content-class="flex flex-col gap-5"
      :loading="isLoading"
    >
      <UIContentBox
        v-if="currentWAM.data"
        class="add-new"
      >
        <FormulateForm
          class="add-new__form flex flex-col gap-1 items-start w-full"
          @submit="saveGeneral"
        >
          <div class="add-new__form-fields flex items-start gap-4 w-full">
            <FormulateInput
              type="text"
              :validation="[
                ['bail'],
                ['required'],
                ['matches', alphanumericWithSpacesRegex],
                ['max', 100],
              ]" 
              v-model="nameModel"
              class="flex-1 w-5/12"
              :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
              :label="$t('fields.name')"
              :placeholder="$t('fields.name')"
              :disabled="isGeneralDisabled"
            />
            <FormulateInput
              type="text"
              :validation="[
                ['bail'],
                ['optional'],
                ['matches', printableAsciiRegex],
                ['max', 255],
              ]" 
              v-model="descriptionModel"
              class="flex-1 w-5/12"
              :element-class="(context, classes) => ['flex-1 min-w-full'].concat(classes)"
              :label="$t('fields.description')"
              :placeholder="$t('fields.description')"
              :disabled="isGeneralDisabled"
            />
          </div>

          <Button
            v-if="isSubmitShown && !isViewMode"
            class="add-new__save-btn flex-shrink-0 w-32 ml-auto"
            type="primary"
            button-type="submit"
            :disabled="!isSaveEnabled"
          >
            {{ $t('actions.save') }}
          </Button>

        </FormulateForm>
      </UIContentBox>

      <UIContentBox
        v-if="currentWAM && (isFullDataLoaded || isCreateMode)"
      >
        <MatrixEditTable
          :disabled="isConfigDisabled"
          :loading="isLoading"
          :matrix-data="currentWAM"
          :readonly="isViewMode"
          @save:row="onRowSave"
        />
      </UIContentBox>

      <!-- Currently, isn't displayed in EDIT mode for existing matrix -->
      <UIContentBox v-if="isSubmitShown">
        <Button
          class="add-new__save-btn flex-shrink-0 w-32 ml-auto"
          type="primary"
          :disabled="!isSubmitEnabled"
          @click="() => saveMatrix({ redirect: true })"
        >
          {{ (isCreateMode || isAfterCreation) ? $t('actions.submit') : $t('actions.save') }}
        </Button>
      </UIContentBox>

      <UIContentBox v-if="!isSubmitShown && !isViewMode">
        <Button
          class="add-new__save-btn flex-shrink-0 w-32 ml-auto"
          type="primary"
          button-type="button"
          :disabled="!isSaveEnabled"
          @click="saveGeneral"
        >
          {{ $t('actions.save') }}
        </Button>
      </UIContentBox>
    </NeoLayout>
  </UIPage>
</template>

<script>
import { mapMutations, mapGetters } from "vuex";
import SubHeader from "@/components/SubHeader";
import Button from "@shared/components/button";
import { checkPermission } from "@shared/utils/functions";
import api from "../api";
import handleError from "@shared/mixins/handleError";
import { MatrixEditTable } from "../components";
import { VIEW_MODE_MAP, REQUIRED_FIELDS } from "../utils/constants";
import { MatrixData } from "../utils/MatrixData";
import {
  alphanumericWithSpacesRegex,
  printableAsciiRegex,
} from "@shared/utils/validation/regex.js";

export default {
  name: "work-allocation-matrix-edit",
  title() {
    return this.$t('components.appsidebar.work_allocation_matrix');
  },
  mixins: [handleError],

  components: {
    SubHeader,
    MatrixEditTable,
    Button,
  },

  props: {},

  data() {
    return {
      alphanumericWithSpacesRegex,
      printableAsciiRegex,
      isLoading: false,
      isConfigLoading: false,
      isFullDataLoaded: false,
      currentWAM: null,
    }
  },

  created() {
    if (this.isCreateMode && this.currentMatrixId) {
      this.$router.push({
        name: "Work Allocation Matrix View",
        params: { action: VIEW_MODE_MAP.CREATE },
      });
    } else if (!this.isCreateMode && !this.currentMatrixId) {
      this.$router.push({
        name: "Work Allocation Matrix List",
      });
    }

    // If not in view mode and edit is not allowed, redirect to list or matrix view page (view mode)
    if (!this.isViewMode && !this.isEditAllowed) {
      if (this.currentMatrixId) {
        this.$router.push({
          name: "Work Allocation Matrix View",
          params: {
            action: VIEW_MODE_MAP.VIEW,
            id: this.currentMatrixId,
          },
        });
      } else {
        this.$router.push({
          name: "Work Allocation Matrix List",
        });
      }
    }
    // Reset selected WAM if create mode
    if (this.isCreateMode) {
      this.setSelectedWAM();
    }
  },

  beforeDestroy() {
    // Reset current WAM when leaving the page
    this.setSelectedWAM();
  },

  // Since we have partial WAM data from the list page, we need to fetch the full matrix data
  async mounted() {
    this.isLoading = true;
    try {
      // set initial WAM data:
      this.initMatrixData();

      if (!this.isCreateMode) {
        await this.fetchCurrentMatrix();
      }

    } catch (error) {
      this.handleError(error);
    } finally {
      this.isLoading = false;
    }
  },

  computed: {
    ...mapGetters({
      getSelectedWAM: "WAMStore/getSelectedWAM",
    }),

    isEditAllowed() {
      return checkPermission("wam.edit");
    },

    isAfterCreation() {
      return this.$route.params.afterCreation || false;
    },

    viewMode() {
      return this.$route.params.action || VIEW_MODE_MAP.CREATE;
    },
    isCreateMode() {
      return this.viewMode === VIEW_MODE_MAP.CREATE;
    },
    isViewMode() {
      return this.viewMode === VIEW_MODE_MAP.VIEW;
    },

    currentMatrixId() {
      return this.$route.params.id || null;
    },
    isGeneralValid() {
      if (!this.currentWAM?.data) return false;
      // Check if all required fields are filled
      return REQUIRED_FIELDS.every(field => this.currentWAM.data[field]);
    },

    isSaveEnabled() {
      if (!this.currentWAM?.data) return false;
      return (!this.currentWAM?.isGeneralSaved && this.isGeneralValid)
        || (!this.currentWAM?.isConfigSaved && !this.isConfigEditing);
    },
    isConfigEditing() {
      if (!this.currentWAM?.data) return true;
      // Check if any config is being edited
      return this.currentWAM.isAnyEditing;
    },
    /**
     * Check if the submit button should be enabled.
     * In this.isAfterCreation state, use this button to submit and leave the screen
     */
    isSubmitEnabled() {
      if (!this.currentWAM?.data) return true;

      return this.currentWAM?.isGeneralSaved
        && !this.isConfigEditing
        && this.isGeneralValid
        && (!this.currentWAM?.isSaved || this.isAfterCreation);
    },
    isSubmitShown() {
      return this.isCreateMode || this.isAfterCreation;
    },
    isGeneralDisabled() {
      if (!this.currentWAM?.data) return true;
      // Disable if general is not saved and config is being edited
      return this.isViewMode || this.isConfigEditing;
    },
    isConfigDisabled() {
      if (!this.currentWAM?.data) return true;
      // Disable if config is not saved and general is being edited
      return !this.currentWAM?.isGeneralSaved || !this.isGeneralValid || this.isViewMode;
    },

    nameModel: {
      get() {
        return this.currentWAM?.data?.name || "";
      },
      set(val) {
        if (this.currentWAM?.data) {
          this.currentWAM.data.name = val;
          if (!this.isLoading)
            this.currentWAM.touch();
        }
      },
    },
    descriptionModel: {
      get() {
        return this.currentWAM?.data?.description || "";
      },
      set(val) {
        if (this.currentWAM?.data) {
          this.currentWAM.data.description = val;
          if (!this.isLoading)
            this.currentWAM.touch();
        }
      },
    },
  },

  methods: {
    ...mapMutations({
      setSelectedWAM: "WAMStore/SET_CURRENT_WAM",
    }),

    async fetchCurrentMatrix() {
      this.isConfigLoading = true;
      try {
        const matrix = await api.getWorkAllocationMatrixItem(this.currentMatrixId);
        this.initMatrixData(matrix);
        this.isFullDataLoaded = true;
      } catch (error) {
        this.handleError(error);
      } finally {
        this.isConfigLoading = false;
      }
    },
    initMatrixData(payload = this.getSelectedWAM) {
      this.currentWAM = new MatrixData(payload, {
        skipValidation: true, // Disable FE validation for the matrix
      });
    },
    async saveGeneral() {
      if (!this.currentWAM?.data) return;

      await this.saveMatrix();

      // Uncomment conditions if we can save general data separately. Comment API call/provide generalOnly=true if otherwise
      if (this.isCreateMode) {
        this.currentWAM.setGeneralSaved();
        this.isFullDataLoaded = true;

        // switch to EDIT mode
        this.$router.push({
          name: "Work Allocation Matrix View",
          params: {
            action: VIEW_MODE_MAP.EDIT,
            id: this.currentWAM.data.id,
            afterCreation: true, // controls the "after creation" state
          },
        });
      }
    },
    async saveMatrix({
      generalOnly = false,
      redirect = false,
    } = {}) {
      if (!this.currentWAM?.data) return;
      try {
        await this.currentWAM.save(generalOnly);

        if (this.isCreateMode) {
          this.$toast.success(this.$t("general.save_success"));
        } else {
          this.$toast.success(this.$t("general.update_success"));
        }

        if (redirect) {
          this.$router.push({
            name: "Work Allocation Matrix List",
          });
        }
      } catch (error) {
        this.handleError(error);
        throw error;
      }
    },
    async onRowSave({ callback }) {
      try {
        // if matrix hasn't been created yet (!!isCreateMode), we can't update it:
        if (!this.isCreateMode) {
          await this.saveMatrix();
        }
        callback();
      } catch (error) {
        console.error("Error saving matrix:", error);
      }
    },
  }
};
</script>
